// Editor da regra de recorrencia: dias da semana + horarios, ou intervalo em
// horas; inicio e fim opcionais (horario de Brasilia). Mostra a descricao e as
// proximas publicacoes calculadas pela mesma funcao do bot.

import { useMemo } from "react";
import { Plus, X } from "lucide-react";
import { describeRecurrence, listOccurrences, MAX_TIMES_PER_DAY } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  MAX_INTERVAL_HOURS,
  MIN_INTERVAL_HOURS,
  WEEKDAY_CHIPS,
  draftEnd,
  draftStart,
  draftToRule,
  toggleDay,
  type RecurrenceDraft,
} from "@/lib/recurrence-draft";

interface RecurrenceEditorProps {
  readonly draft: RecurrenceDraft;
  readonly onChange: (draft: RecurrenceDraft) => void;
}

const PREVIEW_COUNT = 3;
const PREVIEW_WINDOW_MS = 60 * 24 * 60 * 60 * 1000;

function formatWhen(date: Date): string {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** Proximas ocorrencias do rascunho (vazio se invalido). */
export function previewOccurrences(draft: RecurrenceDraft, now: Date = new Date()): Date[] {
  const rule = draftToRule(draft);
  const startsAt = draftStart(draft);
  if (!rule || !startsAt) return [];
  const from = startsAt > now ? startsAt : now;
  return listOccurrences({ rule, startsAt, endsAt: draftEnd(draft) }, from, new Date(from.getTime() + PREVIEW_WINDOW_MS), PREVIEW_COUNT);
}

function ModeToggle({ draft, onChange }: RecurrenceEditorProps): JSX.Element {
  const options: Array<{ mode: RecurrenceDraft["mode"]; label: string }> = [
    { mode: "weekly", label: "Dias e horários" },
    { mode: "interval", label: "A cada X horas" },
  ];
  return (
    <div role="radiogroup" aria-label="Tipo de recorrência" className="inline-flex rounded-lg border border-border p-0.5">
      {options.map((o) => (
        <button
          key={o.mode}
          type="button"
          role="radio"
          aria-checked={draft.mode === o.mode}
          onClick={() => onChange({ ...draft, mode: o.mode })}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            draft.mode === o.mode ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function WeeklyFields({ draft, onChange }: RecurrenceEditorProps): JSX.Element {
  const setTime = (index: number, value: string): void =>
    onChange({ ...draft, times: draft.times.map((t, i) => (i === index ? value : t)) });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" aria-label="Dias da semana">
        {WEEKDAY_CHIPS.map((label, day) => (
          <button
            key={label}
            type="button"
            aria-pressed={draft.days.includes(day)}
            onClick={() => onChange(toggleDay(draft, day))}
            className={cn(
              "h-8 min-w-11 rounded-md border px-2 text-xs font-medium",
              draft.days.includes(day) ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {draft.times.map((time, index) => (
          <div key={index} className="flex items-center gap-1">
            <Input type="time" aria-label={`Horário ${index + 1}`} value={time} onChange={(e) => setTime(index, e.target.value)} className="w-28" />
            {draft.times.length > 1 && (
              <button type="button" aria-label={`Remover horário ${index + 1}`} onClick={() => onChange({ ...draft, times: draft.times.filter((_, i) => i !== index) })} className="rounded p-1 text-muted-foreground hover:text-destructive">
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {draft.times.length < MAX_TIMES_PER_DAY && (
          <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...draft, times: [...draft.times, "18:00"] })}>
            <Plus size={13} /> Horário
          </Button>
        )}
      </div>
    </div>
  );
}

function DateFields({ draft, onChange }: RecurrenceEditorProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div className="space-y-1.5">
        <Label htmlFor="rec-start" variant="field">Início</Label>
        <Input id="rec-start" type="date" value={draft.startDate} onChange={(e) => onChange({ ...draft, startDate: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rec-start-time" variant="field">Hora de início</Label>
        <Input id="rec-start-time" type="time" value={draft.startTime} onChange={(e) => onChange({ ...draft, startTime: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rec-end" variant="field">Fim (opcional)</Label>
        <Input id="rec-end" type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => onChange({ ...draft, endDate: e.target.value })} />
      </div>
    </div>
  );
}

export function RecurrenceEditor({ draft, onChange }: RecurrenceEditorProps): JSX.Element {
  const rule = useMemo(() => draftToRule(draft), [draft]);
  const upcoming = useMemo(() => previewOccurrences(draft), [draft]);
  return (
    <div className="space-y-4 rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.35)] p-4">
      <ModeToggle draft={draft} onChange={onChange} />
      {draft.mode === "weekly" ? (
        <WeeklyFields draft={draft} onChange={onChange} />
      ) : (
        <div className="flex items-center gap-2">
          <Label htmlFor="rec-interval" variant="field">A cada</Label>
          <Input
            id="rec-interval"
            type="number"
            min={MIN_INTERVAL_HOURS}
            max={MAX_INTERVAL_HOURS}
            value={draft.intervalHours}
            onChange={(e) => onChange({ ...draft, intervalHours: Number(e.target.value) })}
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">horas (de {MIN_INTERVAL_HOURS} a {MAX_INTERVAL_HOURS})</span>
        </div>
      )}
      <DateFields draft={draft} onChange={onChange} />
      <div className="text-xs text-muted-foreground" aria-live="polite">
        {rule ? (
          <>
            <p className="font-medium text-foreground">{describeRecurrence(rule)} (horário de Brasília)</p>
            <p>
              {upcoming.length > 0
                ? `Próximas: ${upcoming.map(formatWhen).join(" · ")}`
                : "Nenhuma publicação futura nesse período."}
            </p>
          </>
        ) : (
          <p className="text-destructive">Escolha ao menos um dia e um horário, ou um intervalo válido.</p>
        )}
      </div>
    </div>
  );
}
